import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Loader2, Filter, PlayCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import StatusCell from '@/pages/monitoring/components/StatusCell';
import { fetchCompliance, runDailyCheck } from '@/pages/monitoring/services/monitoring.services';

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const TODAY = toISODate(new Date());
const YESTERDAY = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toISODate(d);
})();

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISODate(d);
};

const PRESETS = [
  { id: 'ayer', label: 'Ayer', range: () => ({ from: YESTERDAY, to: YESTERDAY }) },
  { id: 'hoy', label: 'Hoy', range: () => ({ from: TODAY, to: TODAY }) },
  { id: '7d', label: 'Últimos 7 días', range: () => ({ from: daysAgo(7), to: YESTERDAY }) },
  { id: '30d', label: 'Últimos 30 días', range: () => ({ from: daysAgo(30), to: YESTERDAY }) },
];

/**
 * Matriz centro x tipo de registro. Una sola llamada a /monitoring/compliance
 * cubre los 18 centros y todo el rango: el backend hace una agregación por
 * colección en lugar de una petición por centro y columna.
 */
const ComplianceTab = ({ onNotificationsChanged }) => {
  const { toast } = useToast();
  const [preset, setPreset] = useState('ayer');
  const [from, setFrom] = useState(YESTERDAY);
  const [to, setTo] = useState(YESTERDAY);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [soloIncompletos, setSoloIncompletos] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(
    async (desde, hasta) => {
      setLoading(true);
      try {
        const resp = await fetchCompliance({ from: desde, to: hasta });
        if (!resp?.state) throw new Error(resp?.message || 'Respuesta inválida');
        setData(resp.data);
        setLastRefresh(new Date());
      } catch (e) {
        toast({
          title: 'Error',
          description: e.message || 'No se pudo cargar la completitud de registros.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    load(from, to);
  }, [from, to, load]);

  const applyPreset = (p) => {
    setPreset(p.id);
    const r = p.range();
    setFrom(r.from);
    setTo(r.to);
  };

  const handleGenerarAlertas = async () => {
    setRunning(true);
    try {
      // El chequeo evalúa un día concreto; usamos el fin del rango visible.
      const resp = await runDailyCheck(to);
      const nuevas = resp?.data?.notificacionesNuevas ?? 0;
      const cerradas = resp?.data?.autoResueltas ?? 0;
      const partes = [];
      if (nuevas > 0) partes.push(`${nuevas} nueva(s)`);
      if (cerradas > 0) partes.push(`${cerradas} cerrada(s) por digitación tardía`);
      toast({
        title: `Chequeo de ${to}`,
        description: partes.length
          ? `${partes.join(' · ')}.`
          : 'Sin cambios: los hallazgos existentes no se duplican.',
      });
      onNotificationsChanged?.();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudo ejecutar el chequeo.',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  const recordTypes = data?.recordTypes || [];
  const resumen = data?.resumen || [];
  const diasEsperados = data?.days?.length || 1;

  const rows = useMemo(() => {
    const all = data?.laundries || [];
    if (!soloIncompletos) return all;
    return all.filter((r) => r.avance.completos < r.avance.total);
  }, [data, soloIncompletos]);

  const incompletos = (data?.laundries || []).filter(
    (r) => r.avance.completos < r.avance.total,
  ).length;

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                    preset === p.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Desde</Label>
              <Input
                type="date"
                value={from}
                max={TODAY}
                className="w-40"
                onChange={(e) => {
                  setPreset('custom');
                  setFrom(e.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Hasta</Label>
              <Input
                type="date"
                value={to}
                max={TODAY}
                className="w-40"
                onChange={(e) => {
                  setPreset('custom');
                  setTo(e.target.value);
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <Button
                variant={soloIncompletos ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSoloIncompletos((v) => !v)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Solo incompletos {incompletos > 0 && `(${incompletos})`}
              </Button>
              <Button variant="outline" size="sm" onClick={() => load(from, to)} disabled={loading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <Button size="sm" onClick={handleGenerarAlertas} disabled={running || loading}>
                {running ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlayCircle className="mr-2 h-4 w-4" />
                )}
                Generar alertas de {to}
              </Button>
            </div>
          </div>
          {lastRefresh && (
            <p className="text-xs text-muted-foreground mt-3">
              {diasEsperados === 1
                ? `Día evaluado: ${from}`
                : `${diasEsperados} días evaluados (${from} → ${to})`}
              {' · Actualizado '}
              {lastRefresh.toLocaleTimeString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tarjetas resumen por tipo de registro */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {resumen.map((col) => {
          const pct = col.total > 0 ? (col.ok / col.total) * 100 : 0;
          const pendientes = col.total - col.ok;
          return (
            <Card key={col.key} className="border border-border">
              <CardContent className="pt-3 pb-3">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2 line-clamp-2 min-h-[28px]">
                  {col.label}
                </p>
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <p className="text-2xl font-semibold text-foreground tabular-nums">{col.ok}</p>
                    <p className="text-xs text-muted-foreground">de {col.total}</p>
                  </div>
                  {pendientes > 0 && (
                    <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                      {pendientes}
                    </Badge>
                  )}
                </div>
                <div className="h-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      pct === 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500/70'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Matriz */}
      <Card>
        <CardContent className="pt-4">
          {loading && !data ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-3 text-sm text-muted-foreground">Cargando centros…</span>
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">
              {soloIncompletos
                ? 'Todos los centros están al día en este rango.'
                : 'No hay centros de lavado activos.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide w-56 sticky left-0 bg-card">
                      Centro de lavado
                    </th>
                    {recordTypes.map((rt) => (
                      <th
                        key={rt.key}
                        className="text-center py-2.5 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wide min-w-[110px]"
                      >
                        {rt.label}
                      </th>
                    ))}
                    <th className="text-center py-2.5 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      Avance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {rows.map((row, idx) => {
                    const { completos, total } = row.avance;
                    const allDone = total > 0 && completos === total;
                    return (
                      <motion.tr
                        key={row.laundryId}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: Math.min(idx * 0.02, 0.4) }}
                        className="hover:bg-muted/20 transition-colors"
                      >
                        <td className="py-3 px-4 sticky left-0 bg-card">
                          <p className="font-medium text-foreground">{row.nombre}</p>
                          {row.ciudad && (
                            <p className="text-xs text-muted-foreground mt-0.5">{row.ciudad}</p>
                          )}
                        </td>
                        {recordTypes.map((rt) => {
                          const celda = row.celdas?.[rt.key];
                          return (
                            <td key={rt.key} className="py-3 px-3 text-center">
                              <StatusCell
                                estado={celda?.estado}
                                registros={celda?.registros}
                                diasConRegistro={celda?.diasConRegistro}
                                diasEsperados={celda?.diasEsperados || diasEsperados}
                              />
                            </td>
                          );
                        })}
                        <td className="py-3 px-4 text-center">
                          {total === 0 ? (
                            <span className="text-xs text-muted-foreground/40">—</span>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <span
                                className={`text-sm font-semibold tabular-nums ${
                                  allDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                                }`}
                              >
                                {completos}/{total}
                              </span>
                              <div className="w-16 h-1 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${
                                    allDone ? 'bg-emerald-500' : 'bg-amber-500'
                                  }`}
                                  style={{ width: `${(completos / total) * 100}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceTab;
